import { DeployEnv } from '@patsnap-utils/biz';
import type { IPlanConfig, LsEditionType } from '../types';
import { REGION } from '@eureka/utils/environment';

export enum EurekaVersion {
    FREE = 'free',
    AIR = 'air',
    IP = 'ip',
    PROTECT = 'protect',
    MATERIAL = 'material',
    INSIGHTS = 'insights',
    LS = 'ls'
}

export enum EditionType {
    /**
     * 老
     */
    FREE = 'free',
    PREMIUM = 'premium',

    /**
     * 新
     */
    OVERLIMIT_RECOMMAND = 'overlimit' // 次数用量超限推荐卡片
}

export enum UpgradeUsageType {
    ONBOARDING = 'onboarding',
    TRY_PREMIUM = 'tryPremium',
    TRIAL_END = 'trialEnd'
}

export enum UsageAmountWhere {
    HOME_BANNER = 'home_banner',
    TECH_SEEK = 'tech_seek',
    TECH_RESEARCH = 'technical_research',
    TECH_MIND = 'tech_mind',
    MATERIAL_MIND = 'material_tech_mind',
    TECH_RISK = 'tech_fmea',
    MONITOR = 'monitor',
    EUREKA_AGENT = 'eureka_agent',
    IDEA = 'idea',
    PROJECT = 'project',
    TRIZ = 'triz',
    // NOTE: 以下这些是Life Sciences Agent场景
    LS_CHAT_AGENT = 'ls_chat_agent',
    LS_DOC_AGENT = 'ls_document_agent',
    LS_MARKUSH_DRAFT_AGENT = 'ls_markush_draft_agent',
    LS_SAR_AGENT = 'ls_sar_agent'
}

export const AIR_UPGRADE_EVENT_TYPE = 'SS Eureka Upgrade Page';

// 升级所有埋点source_type
export enum BiTrackPoint {
    AIR_ONBOARDING_UPGRADE_CLICK = 'AIR-onboarding-upgrade-click', // onbording场景触发的升级
    AIR_USAGE_UPGRADE_SHOW = 'AIR-usage-upgrade-show', // 免费版使用量超限弹出
    AIR_USAGE_UPGRADE_CLICK = 'AIR-usage-upgrade-click', // 免费版使用量超限触发的升级
    AIR_HEADER_UPGRADE_CLICK = 'AIR-header-upgrade-click', // 试用版侧栏的主动升级
    AIR_BANNER_UPGRADE_CLICK = 'AIR-banner-upgrade-click', // 试用版横幅的主动升级
    AIR_TRIALEND_UPGRADE_CLICK = 'AIR-trialend-upgrade-click', // 试用版到期弹窗触发的升级
    AIR_USERSETTING_UPGRADE_SHOW = 'AIR-usersetting-upgrade-show', // 用户设置版本页面的显示
    AIR_USERSETTING_UPGRADE_CLICK = 'AIR-usersetting-upgrade-click', // 用户设置版本页面的升级
    AIR_UPGRADE_CLUEREPORT_SUBMIT = 'AIR-upgrade-cluereport-submit', // 中国区升级线索提交成功
    AIR_UPGRADE_UNLOCK_FORM_SHOW = 'AIR-upgrade-unlock-form-show', // 升级解锁表单展示
    AIR_UPGRADE_UNLOCK_FORM_SUBMIT = 'AIR-upgrade-unlock-form-submit' // 升级解锁表单提交
}

export const isSandBoxEnv = [DeployEnv.CI, DeployEnv.QA, DeployEnv.STAGE, DeployEnv.DEV].includes(REGION)

const PLAN_CONFIG_MAP_400: Record<LsEditionType, IPlanConfig> = {
    personal_basic: {
        credits: 10,
        originalPrice: 0,
        salePrice: 0,
    },
    personal_pro: {
        credits: 100,
        originalPrice: 398,
        salePrice: 400, // (月付的价格)
        yearlyOriginalPrice: 4320, // 360 * 12
        yearlySalePrice: 360, // (PM 给的价格)
        emergencyPackOriginalPrice: 25,
        emergencyPackSaleDiscount: 20,
        emergencyPackSalePrice: 20,
        projectPackOriginalPrice: 55,
        projectPackSalePrice: 44,
        projectPackSaleDiscount: 20,
    },
}

const PLAN_CONFIG_MAP_100: Record<LsEditionType, IPlanConfig> = {
    personal_basic: {
        credits: 10,
        originalPrice: 0,
        salePrice: 0,
    },
    personal_pro: {
        credits: 100,
        originalPrice: 398,
        salePrice: 100, // (月付的价格)
        yearlyOriginalPrice: 1080, // 90 * 12
        yearlySalePrice: 90, // (PM 给的价格)
        emergencyPackOriginalPrice: 25,
        emergencyPackSaleDiscount: 20,
        emergencyPackSalePrice: 20,
        projectPackOriginalPrice: 55,
        projectPackSalePrice: 44,
        projectPackSaleDiscount: 20,
    },
}

export const PLAN_CONFIG = {
    A: PLAN_CONFIG_MAP_400,
    B: PLAN_CONFIG_MAP_100,
}

/**
 * $400 stripe订阅配置
 */
const LS_PRICE_ID_MAP_400: Partial<Record<DeployEnv, Record<string, string>>> = {
    [DeployEnv.STAGE]: {
        monthly: 'price_1SzY7R4BHjfY2tkoL0NlzKbm',
        yearly: 'price_1SzYTl4BHjfY2tkorTPuPatb',
    },
    [DeployEnv.US]: {
        monthly: 'price_1T7otA3n4XHnBaSm6MQ8LLJ8',
        yearly: 'price_1T7otX3n4XHnBaSmZNmAXrl9',
    },
    [DeployEnv.EU]: {
        monthly: 'price_1TBVLF3n4XHnBaSmoMAX62we',
        yearly: 'price_1TBVLe3n4XHnBaSmaq8V4ivJ',
    },
}

/**
 * $100 stripe订阅配置
 */
const LS_PRICE_ID_MAP_100: Partial<Record<DeployEnv, Record<string, string>>> = {
    [DeployEnv.STAGE]: {
        monthly: 'price_1TZm054BHjfY2tkoh1Wy3AdT',
        yearly: 'price_1TZm0P4BHjfY2tkoTMgg0bPl',
    },
    [DeployEnv.US]: {
        monthly: 'price_1TZlpM3n4XHnBaSm1YQDaUSp',
        yearly: 'price_1TZlqP3n4XHnBaSmtoeOZCDA',
    },
    [DeployEnv.EU]: {
        monthly: 'price_1TZlyJ3n4XHnBaSmiVLTWamg',
        yearly: 'price_1TZlyb3n4XHnBaSmHmH5SpqD',
    },
}

/**
 * 套餐价格表
 */
const PRICE_MAP_400: Record<LsEditionType, { priceId: string, billingCycle: 'monthly' | 'yearly' }[]> = {
    personal_basic: [],
    personal_pro: [
        {
            priceId: LS_PRICE_ID_MAP_400[REGION]?.monthly || LS_PRICE_ID_MAP_400[DeployEnv.STAGE]?.monthly || '',
            billingCycle: 'monthly',
        },
        {
            priceId: LS_PRICE_ID_MAP_400[REGION]?.yearly || LS_PRICE_ID_MAP_400[DeployEnv.STAGE]?.yearly || '',
            billingCycle: 'yearly',
        }
    ],
}

/**
 * 套餐价格表
 */
const PRICE_MAP_100: Record<LsEditionType, { priceId: string, billingCycle: 'monthly' | 'yearly' }[]> = {
    personal_basic: [],
    personal_pro: [
        {
            priceId: LS_PRICE_ID_MAP_100[REGION]?.monthly || LS_PRICE_ID_MAP_100[DeployEnv.STAGE]?.monthly || '',
            billingCycle: 'monthly',
        },
        {
            priceId: LS_PRICE_ID_MAP_100[REGION]?.yearly || LS_PRICE_ID_MAP_100[DeployEnv.STAGE]?.yearly || '',
            billingCycle: 'yearly',
        }
    ],
}

/**
 * 针对LS的AB订阅测试系统
 * A 为 Pro $400
 * B 为 Pro $100
 */
type PLG_PRICE_TYPE = 'A' | 'B'

/**
 * 提供外部获取不同订阅的stripe配置
 */
export const PRICE_MAP: Record<PLG_PRICE_TYPE, Record<LsEditionType, { priceId: string, billingCycle: 'monthly' | 'yearly' }[]>> = {
    A: PRICE_MAP_400,
    B: PRICE_MAP_100,
}
